import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from '@/lib/router-compat';
import { brandSlugFromMake } from '@/utils/brandSlug';
import { formatMakeDisplay } from '@/utils/makeDisplay';
import { slugifyPlace, cityLinkLabel } from '@/utils/cityArea';
import { getCityBySlug } from '@/data/cities';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { buildListingSlug } from '@/utils/listingSlug';
import { displayModelName } from '@/data/modelPages';
import CanonicalSlugSync from '@/components/listings/forms/CanonicalSlugSync';
import { CarDetailSkeleton } from '@/components/performance/LoadingSkeleton';
import { Badge } from '@/components/ui/badge';
import { BadgeCheck, Calendar, Eye, ChevronRight, BarChart3, User } from 'lucide-react';
import { GalleryV2 } from '@/components/listings/detail/GalleryV2';
import { QuickSpecsV2 } from '@/components/listings/detail/QuickSpecsV2';
import { PriceFairnessBadge } from '@/components/listings/detail/PriceFairnessBadge';
import { PriceBenchmarkBar } from '@/components/listings/detail/PriceBenchmarkBar';
import { VehicleHistoryV2 } from '@/components/listings/detail/VehicleHistoryV2';
import { DealerCardV2 } from '@/components/listings/detail/DealerCardV2';
import { PaymentCalculatorV2 } from '@/components/listings/detail/PaymentCalculatorV2';
import { ReservationModal } from '@/components/listings/detail/ReservationModal';
import { ListingPriceAlertCard } from '@/components/listings/detail/ListingPriceAlertCard';
import { PriceHistoryCard } from '@/components/listings/detail/PriceHistoryCard';
import { MobileContactBar } from '@/components/listings/detail/MobileContactBar';
import { SimilarListings } from '@/components/listings/card/SimilarListings';
import { LocationMapCard } from '@/components/listings/detail/LocationMapCard';
import { FormattedDescription } from '@/components/listings/detail/FormattedDescription';
import { AutoCompareServicesWidget } from '@/components/listings/detail/AutoCompareServicesWidget';
import { Button } from '@/components/ui/button';
import { useLang } from '@/i18n/useLang';
import {
  type AttrLang,
  displayBodyType,
  displayColor,
  displayDrivetrain,
  displayEuroNorm,
  displayFeature,
  displayFuel,
  displayInterior,
  displayTransmission,
} from '@/utils/attributeDisplay';
import { inferServiceHistoryComplete } from '@/utils/serviceHistory';
import { setAssistantListingContext } from '@/utils/assistantContext';
import { trackListingView } from '@/utils/contactTracking';
import { useRouteLoaderData } from '@/lib/seo/loader-data';
import { buildBreadcrumbLd } from "@/utils/schemaMarkup";
import { useFxRatePerEur } from '@/hooks/useFxRatePerEur';



const DETAIL_COPY = {
  ro: {
    notFound: 'Anunțul nu a fost găsit.',
    backToSearch: 'Înapoi la căutare',
    home: 'Acasă',
    usedCars: 'Mașini second hand',
    addedToday: 'Adăugat azi',
    addedDaysAgoOne: (d: number) => `Adăugat acum ${d} zi`,
    addedDaysAgoMany: (d: number) => `Adăugat acum ${d} zile`,
    viewsOne: 'vizualizare',
    viewsMany: 'vizualizări',
    verifiedDealer: 'Dealer verificat',
    sold: 'Vândută',
    techSpecs: 'Specificații tehnice',
    mainFeatures: 'Dotări principale',
    description: 'Descriere',
    inquirySubject: 'Întrebare',
    bodyType: 'Caroserie', color: 'Culoare', engine: 'Motor', interior: 'Interior',
    transmission: 'Transmisie', doors: 'Nr. portiere', drivetrain: 'Tracțiune',
    euroNorm: 'Norme emisii', consumption: 'Consum mixt', power: 'Putere',
  },
  en: {
    notFound: 'Listing not found.',
    backToSearch: 'Back to search',
    home: 'Home',
    usedCars: 'Used cars',
    addedToday: 'Added today',
    addedDaysAgoOne: (d: number) => `Added ${d} day ago`,
    addedDaysAgoMany: (d: number) => `Added ${d} days ago`,
    viewsOne: 'view',
    viewsMany: 'views',
    verifiedDealer: 'Verified dealer',
    sold: 'Sold',
    techSpecs: 'Technical specifications',
    mainFeatures: 'Main features',
    description: 'Description',
    inquirySubject: 'Inquiry',
    bodyType: 'Body type', color: 'Colour', engine: 'Engine', interior: 'Interior',
    transmission: 'Transmission', doors: 'Doors', drivetrain: 'Drivetrain',
    euroNorm: 'Emission class', consumption: 'Combined consumption', power: 'Power',
  },
  de: {
    notFound: 'Anzeige nicht gefunden.',
    backToSearch: 'Zurück zur Suche',
    home: 'Startseite',
    usedCars: 'Gebrauchtwagen',
    addedToday: 'Heute hinzugefügt',
    addedDaysAgoOne: (d: number) => `Vor ${d} Tag hinzugefügt`,
    addedDaysAgoMany: (d: number) => `Vor ${d} Tagen hinzugefügt`,
    viewsOne: 'Aufruf',
    viewsMany: 'Aufrufe',
    verifiedDealer: 'Geprüfter Händler',
    sold: 'Verkauft',
    techSpecs: 'Technische Daten',
    mainFeatures: 'Hauptausstattung',
    description: 'Beschreibung',
    inquirySubject: 'Anfrage',
    bodyType: 'Karosserie', color: 'Farbe', engine: 'Motor', interior: 'Innenraum',
    transmission: 'Getriebe', doors: 'Türen', drivetrain: 'Antrieb',
    euroNorm: 'Schadstoffklasse', consumption: 'Verbrauch kombiniert', power: 'Leistung',
  },
} as const;

type Listing = {
  id: string;
  make: string;
  model: string;
  year: number;
  price: number;
  currency: string;
  trim_level?: string | null;
  mileage_value?: number | null;
  location_city?: string | null;
  location_region?: string | null;
  photos: string[];
  description_text?: string | null;
  attributes: any;
  status: string;
  user_id?: string | null;
  category_id: string;
  dealer_verified?: boolean | null;
  created_at: string;
};

export default function CarDetailV3() {
  const params = useParams<{ id?: string; slug?: string }>();
  const raw = (params.id || params.slug || '').toString();
  // Slug format: make-model-year-city-{uuid}. Extract trailing UUID if present.
  const uuidMatch = raw.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
  const id = uuidMatch ? uuidMatch[0].toLowerCase() : raw;
  const navigate = useNavigate();
  const lang = useLang();
  const t = DETAIL_COPY[lang];
  const { data: ronPerEur } = useFxRatePerEur('RON');
  const attrLang: AttrLang = (lang === 'en' || lang === 'de') ? lang : 'ro';

  // Seeded from the route loader so the server HTML already contains the
  // vehicle specs, price and seller block (crawlers without JS see them too).
  const loaderData = useRouteLoaderData<{ id?: string; row?: Listing | null }>();
  const ssrListing = loaderData?.row && (loaderData.row as Listing).id === id
    ? (loaderData.row as Listing)
    : null;

  const [listing, setListing] = useState<Listing | null>(ssrListing);
  // Display-only model name (stored data unchanged), same rule as the model page card.
  const shownModel = listing ? displayModelName(listing as any) : '';
  const [contact, setContact] = useState<{ phone?: string; email?: string }>({});
  const [viewsCount, setViewsCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(!ssrListing);
  const [reserveOpen, setReserveOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    let canceled = false;
    (async () => {
      setLoading((prev) => (listing ? false : prev));
      const [{ data }, { data: contactRows }, { data: viewsData }] = await Promise.all([
        supabase.from('listings_v2').select('*').eq('id', id).maybeSingle(),
        supabase.rpc('get_public_contact_info', { _listing_id: id }),
        supabase.rpc('get_listing_view_count' as any, { _listing_id: id, _since_days: null }),
      ]);
      if (canceled) return;
      if (data) {
        setListing(data as any);
        if (contactRows && contactRows.length) {
          setContact({
            phone: (contactRows[0] as any).contact_phone || undefined,
            email: (contactRows[0] as any).contact_email || undefined,
          });
        }
        setViewsCount(((viewsData as number | null) ?? 0));

        // O singură vizualizare per anunț per tab (vezi contactTracking).
        trackListingView({ id: data.id, sellerId: (data as any).user_id, sellerType: (data as any).seller_type });
      }
      setLoading(false);
    })();
    return () => { canceled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Asistentul AI primește datele mașinii de pe pagina curentă.
  useEffect(() => {
    if (!listing) return;
    const a = listing.attributes || {};
    setAssistantListingContext({
      id: listing.id,
      title: `${listing.make} ${shownModel}${listing.trim_level ? ' ' + listing.trim_level : ''}`,
      year: listing.year ?? null,
      price: listing.price ?? null,
      currency: listing.currency ?? 'EUR',
      mileage: listing.mileage_value ?? null,
      fuel: a.fuel_type || a.fuel || null,
      transmission: a.transmission || null,
      location: listing.location_city || null,
      sellerType: (listing as any).seller_type || null,
      description: listing.description_text ? String(listing.description_text).slice(0, 1500) : null,
    });
    return () => setAssistantListingContext(null);
  }, [listing]);


  if (loading) return <CarDetailSkeleton />;
  if (!listing) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <p className="text-muted-foreground">{t.notFound}</p>
        <Button onClick={() => navigate('/vehicles')} className="mt-4">{t.backToSearch}</Button>
      </div>
    );
  }

  const attrs = listing.attributes || {};
  const title = `${listing.make} ${shownModel}${listing.trim_level ? ' ' + listing.trim_level : ''}`;
  const subTitle = [
    listing.year,
    listing.mileage_value ? `${listing.mileage_value.toLocaleString('ro-RO')} km` : null,
    displayFuel(attrs.fuel_type || attrs.fuel, attrLang),
    displayTransmission(attrs.transmission, attrLang),
    listing.location_city,
  ].filter(Boolean).join(' · ');

  const fmtPrice = `${Math.round(listing.price).toLocaleString('ro-RO')} ${listing.currency}`;
  const fmtRON = listing.currency === 'EUR' && ronPerEur ? `~${Math.round(listing.price * ronPerEur).toLocaleString('ro-RO')} RON` : null;

  const daysAgo = Math.max(0, Math.floor((Date.now() - new Date(listing.created_at).getTime()) / 86400000));

  const pageTitle = `${title} · ${listing.year} · ${fmtPrice} · RO Auto Market`;
  const metaDesc = `${title} ${listing.year}, ${listing.mileage_value || ''} km, ${fmtPrice}. ${listing.location_city || ''}`.trim();
  const pageUrl = `https://roautomarket.ro/listing/${buildListingSlug(listing as any)}`;
  const primaryPhoto = Array.isArray(listing.photos) && listing.photos.length > 0
    ? (typeof listing.photos[0] === 'string' ? listing.photos[0] : (listing.photos[0] as any)?.url)
    : null;
  const ogImage = primaryPhoto && /^https?:\/\//.test(primaryPhoto)
    ? primaryPhoto
    : 'https://roautomarket.ro/og-roautomarket.png';

  const brandSlug = brandSlugFromMake(listing.make);
  const citySlug = listing.location_city ? slugifyPlace(listing.location_city) || null : null;
  const breadcrumbLd = buildBreadcrumbLd([
    { name: t.home, item: "https://roautomarket.ro/" },
    { name: t.usedCars, item: "https://roautomarket.ro/vehicles" },
    listing.make ? { name: listing.make, item: brandSlug ? `https://roautomarket.ro/cars/${brandSlug}` : `https://roautomarket.ro/vehicles?make=${encodeURIComponent(listing.make)}` } : null,
    { name: `${listing.make || ''} ${shownModel || ''} ${listing.year || ''}`.trim() || title, item: pageUrl },
  ]);

  return (
    <>
      <CanonicalSlugSync canonicalSlug={buildListingSlug(listing as any)} />
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={`${title} ${listing.year} | ${fmtPrice}`} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${title} ${listing.year} | ${fmtPrice}`} />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image" content={ogImage} />
        {breadcrumbLd && <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": ["Product", "Car"],
          "name": `${title} ${listing.year}`,
          "description": metaDesc,
          "url": pageUrl,
          "image": (Array.isArray(listing.photos) ? listing.photos : [])
            .map((p: any) => typeof p === 'string' ? p : p?.url)
            .filter((u: any) => typeof u === 'string' && /^https?:\/\//.test(u))
            .slice(0, 8),
          "sku": listing.id,
          "brand": listing.make ? { "@type": "Brand", "name": listing.make } : undefined,
          "model": listing.model || undefined,
          "vehicleModelDate": listing.year ? String(listing.year) : undefined,
          "vehicleTransmission": attrs.transmission || undefined,
          "fuelType": attrs.fuel_type || attrs.fuel || undefined,
          "bodyType": attrs.body_type || attrs.body || undefined,
          "color": attrs.color || undefined,
          "mileageFromOdometer": listing.mileage_value ? {
            "@type": "QuantitativeValue",
            "value": listing.mileage_value,
            "unitCode": "KMT"
          } : undefined,
          "offers": {
            "@type": "Offer",
            "price": Math.round(listing.price),
            "priceCurrency": listing.currency || "EUR",
            "availability": listing.status === 'sold'
              ? "https://schema.org/SoldOut"
              : "https://schema.org/InStock",
            "itemCondition": "https://schema.org/UsedCondition",
            "url": pageUrl,
            "availableAtOrFrom": listing.location_city ? {
              "@type": "Place",
              "address": { "@type": "PostalAddress", "addressLocality": listing.location_city, "addressCountry": "RO" }
            } : undefined
          }
        })}</script>
      </Helmet>

      <div className="container mx-auto px-4 pt-4 pb-[100px] lg:pb-12">
        {/* Breadcrumb */}
        <div className="surface-dark band-bleed -mt-4 mb-4 py-3 hidden md:block">
        <nav className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
          <Link to="/" className="hover:text-foreground">{t.home}</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/vehicles" className="hover:text-foreground">{t.usedCars}</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to={brandSlug ? `/cars/${brandSlug}` : `/vehicles?make=${encodeURIComponent(listing.make)}`} className="hover:text-foreground">{listing.make}</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground truncate max-w-[200px]">{shownModel} {listing.year}</span>
        </nav>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
          {/* Gallery — full width on mobile */}
          <div className="order-1 lg:order-none lg:col-span-8 lg:col-start-1">
            <GalleryV2 photos={listing.photos || []} title={title} videoUrl={(listing as any).video_url || (listing as any).attributes?.video_url} containFirstPhoto={listing.id === '512e5988-e88e-4ba1-88c0-39f537bcfcf6'} />
          </div>

          {/* Title + price — stacked on mobile, side-by-side on desktop */}
          <div className="order-2 lg:order-none lg:col-span-8 lg:col-start-1">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-2xl md:text-3xl font-semibold text-foreground leading-tight">{[title, listing.year, listing.location_city].filter(Boolean).join(' · ')}</h1>
                  {listing.status === 'sold' && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-brand-green text-brand-green-foreground text-xs font-bold tracking-wider shadow-xs">
                      {t.sold}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">{subTitle}</p>
              </div>
              <div className="md:text-right">
                <div className={`text-3xl md:text-3xl font-semibold text-foreground ${listing.status === 'sold' ? 'line-through opacity-60' : ''}`}>{fmtPrice}</div>
                {fmtRON && <div className={`text-xs text-muted-foreground mt-0.5 ${listing.status === 'sold' ? 'line-through opacity-60' : ''}`}>{fmtRON}</div>}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 items-center">
              <PriceFairnessBadge listingId={listing.id} />
              <a
                href={`https://autocompare.ro/din-roautomarket?listingId=${encodeURIComponent(listing.id)}&listingUrl=${encodeURIComponent(`https://roautomarket.ro/listing/${listing.id}`)}&utm_source=roautomarket&utm_medium=listing_page&utm_campaign=price_analysis`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border bg-background hover:border-destructive hover:shadow-xs transition text-xs font-medium text-foreground"
              >
                <BarChart3 className="h-3.5 w-3.5" />
                Vezi analiza de preț pe AutoCompare
              </a>
            </div>
            <div className="mt-4">
              <PriceBenchmarkBar listingId={listing.id} />
            </div>
          </div>

          {/* Quick specs */}
          <div className="order-3 lg:order-none lg:col-span-8 lg:col-start-1">
            <QuickSpecsV2
              year={listing.year}
              mileageKm={listing.mileage_value || undefined}
              enginePowerHp={attrs.engine_power || attrs.power_hp}
              fuelType={attrs.fuel_type || attrs.fuel}
              transmission={attrs.transmission}
              bodyType={attrs.body_type}
              engineCc={attrs.engine_cc}
            />
          </div>

          {/* Trust pills — wrap on all sizes so nothing is cut off on mobile */}
          <div className="order-4 lg:order-none lg:col-span-8 lg:col-start-1">
            <div className="flex flex-wrap gap-2 text-[11px] sm:text-xs">

              {(listing as any).seller_type === 'private' && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted text-muted-foreground border border-border shrink-0">
                  <User className="h-3.5 w-3.5" /> Vânzător privat
                </span>
              )}
              {(listing as any).seller_type !== 'private' && listing.dealer_verified && (
                <Link
                  to="/incredere/verificare-dealeri"
                  title="Cum verificăm dealerii (RISCO + ANAF)"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-green/10 text-brand-green border border-brand-green/30 shrink-0 hover:bg-brand-green/20 transition-colors cursor-pointer"
                >
                  <BadgeCheck className="h-3.5 w-3.5" /> {t.verifiedDealer}
                </Link>
              )}

              {attrs.mot_valid_until && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-green/10 text-brand-green border border-brand-green/30 shrink-0">
                  ITP {new Date(attrs.mot_valid_until).getFullYear()}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground border border-border shrink-0">
                <Calendar className="h-3.5 w-3.5" /> {daysAgo === 0 ? t.addedToday : (daysAgo === 1 ? t.addedDaysAgoOne(daysAgo) : t.addedDaysAgoMany(daysAgo))}
              </span>
              {viewsCount !== null && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted text-muted-foreground border border-border shrink-0">
                  <Eye className="h-3.5 w-3.5" /> {viewsCount.toLocaleString()} {viewsCount === 1 ? t.viewsOne : t.viewsMany}
                </span>
              )}
            </div>
          </div>

          {/* Dealer card */}
          <div className="order-5 lg:order-none lg:col-span-4 lg:col-start-9 lg:row-start-1">
            <DealerCardV2
              userId={listing.user_id || undefined}
              phone={contact.phone}
              email={contact.email}
              fallbackLocation={[listing.location_city, listing.location_region].filter(Boolean).join(', ')}
              sellerType={(listing as any).seller_type}
              listingId={listing.id}
              onReserveClick={() => setReserveOpen(true)}
              onMessageClick={() => {
                setReserveOpen(true);
              }}
            />
          </div>

          {/* Payment calculator */}
          {(() => {
            // Calculator works in EUR: RON prices convert via fx_rates; hidden when no rate.
            const cur = (listing.currency || 'EUR').toUpperCase();
            const price = Number(listing.price);
            const calcEur = cur === 'EUR' ? price : cur === 'RON' && ronPerEur ? price / ronPerEur : null;
            const calcRon = cur === 'RON' ? price : ronPerEur && calcEur != null ? calcEur * ronPerEur : null;
            if (calcEur == null || !(calcEur > 0)) return null;
            return (
              <div className="order-6 lg:order-none lg:col-span-4 lg:col-start-9">
                <PaymentCalculatorV2
                  priceEur={calcEur}
                  priceRon={calcRon}
                  listingId={listing.id}
                  make={listing.make}
                  model={listing.model}
                  trim={listing.trim_level || undefined}
                  year={listing.year}
                  fuel={attrs.fuel_type as string | undefined}
                  mileage={listing.mileage_value || undefined}
                  currency={listing.currency}
                  title={title}
                />
              </div>
            );
          })()}

          {/* Tech specs */}
          <section className="order-7 lg:order-none lg:col-span-8 lg:col-start-1">
            <h2 className="text-lg font-semibold text-foreground mb-3">{t.techSpecs}</h2>
            <div className="rounded-xl border border-border bg-card divide-y divide-border">
              <SpecRow label={t.bodyType} value={displayBodyType(attrs.body_type || attrs.tip_caroserie, attrLang)} />
              <SpecRow label={t.color} value={displayColor(attrs.color || attrs.culoare, attrLang)} />
              <SpecRow label={t.engine} value={attrs.engine_displacement ? `${(attrs.engine_displacement / 1000).toFixed(1)}d` : null} />
              <SpecRow label={t.interior} value={displayInterior(attrs.interior, attrLang)} />
              <SpecRow label={t.transmission} value={displayTransmission(attrs.transmission, attrLang)} />
              <SpecRow label={t.doors} value={attrs.doors || attrs.numar_portiere} />
              <SpecRow label={t.drivetrain} value={displayDrivetrain(attrs.drivetrain, attrLang)} />
              <SpecRow label={t.euroNorm} value={displayEuroNorm(attrs.euro_norm)} />

              <SpecRow label={t.consumption} value={attrs.fuel_consumption ? `${attrs.fuel_consumption} l/100km` : null} />
              <SpecRow label={lang === 'en' ? 'Power' : lang === 'de' ? 'Leistung' : 'Putere'} value={attrs.engine_power ? `${attrs.engine_power} ${lang === 'de' ? 'PS' : (lang === 'en' ? 'hp' : 'CP')}` : null} />
              <SpecRow label={lang === 'en' ? 'VIN' : lang === 'de' ? 'Fahrgestellnummer (VIN)' : 'Serie șasiu (VIN)'} value={(listing as any).vin || attrs.vin || null} />
              {attrs.technical_specs && typeof attrs.technical_specs === 'object' &&
                Object.entries(attrs.technical_specs as Record<string, any>)
                  .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
                  .map(([k, v]) => (
                    <SpecRow key={k} label={k} value={String(v)} />
                  ))
              }
            </div>
          </section>

          {/* Features chips */}
          {Array.isArray(attrs.equipment || attrs.features) && (attrs.equipment || attrs.features).length > 0 && (
            <section className="order-8 lg:order-none lg:col-span-8 lg:col-start-1">
              <h2 className="text-lg font-semibold text-foreground mb-3">{t.mainFeatures}</h2>
              <div className="flex flex-wrap gap-2">
                {(attrs.equipment || attrs.features).slice(0, 24).map((f: string) => (
                  <Badge key={f} variant="outline" className="font-normal">{displayFeature(f, attrLang)}</Badge>
                ))}
              </div>
            </section>
          )}

          {/* Description */}
          {listing.description_text && (
            <section className="order-9 lg:order-none lg:col-span-8 lg:col-start-1">
              <h2 className="text-lg font-semibold text-foreground mb-3">{t.description}</h2>
              <FormattedDescription description={listing.description_text} />
            </section>
          )}

          <p className="text-sm text-muted-foreground">
            Ai întrebări despre mașină?{' '}
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event('open-ai-assistant'))}
              className="underline underline-offset-2 text-foreground hover:text-primary"
            >
              Întreabă asistentul.
            </button>
          </p>

          {/* AutoCompare services widget */}
          <div className="order-9 lg:order-none lg:col-span-8 lg:col-start-1">
            <AutoCompareServicesWidget
              make={listing.make}
              model={listing.model}
              year={listing.year}
              price={listing.price ?? undefined}
              listingId={listing.id}
            />
          </div>


          {/* Vehicle history */}
          <div className="order-10 lg:order-none lg:col-span-8 lg:col-start-1">
            <VehicleHistoryV2
              ownerCount={attrs.owner_count ?? (listing as any).numar_proprietari ?? undefined}
              motValidUntil={(listing as any).mot_valid_until ?? attrs.mot_valid_until ?? (attrs as any).itp_valid_until ?? (listing as any).itp_valabil_pana ?? undefined}
              serviceHistoryComplete={inferServiceHistoryComplete(
                attrs.service_history_complete ?? (listing as any).carte_service ?? undefined,
                listing.description_text,
              )}

              accidentFree={attrs.accident_free ?? ((listing as any).interventii_caroserie === true ? false : (listing as any).interventii_caroserie === false ? true : undefined)}
              mileageKm={listing.mileage_value ?? undefined}
            />
          </div>

          {/* Location map */}
          <div className="order-11 lg:order-none lg:col-span-4 lg:col-start-9">
            <LocationMapCard
              city={listing.location_city}
              region={listing.location_region}
            />
          </div>

          {/* Price history (only after a ≥1% drop) */}
          <div className="order-12 lg:order-none lg:col-span-4 lg:col-start-9 empty:hidden">
            <PriceHistoryCard listingId={listing.id} />
          </div>

          {/* Price alert */}
          <div className="order-12 lg:order-none lg:col-span-4 lg:col-start-9">
            <ListingPriceAlertCard
              listingId={listing.id}
              make={listing.make}
              model={listing.model}
              currentPrice={Number(listing.price)}
              currency={listing.currency}
            />
          </div>
        </div>

        {/* Related pages: brand and locality (real canonical URLs, in the server HTML) */}
        {(brandSlug || citySlug) && (
          <nav aria-label="Pagini relevante" className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm">
            {brandSlug && (
              <Link to={`/cars/${brandSlug}`} className="text-primary underline underline-offset-4">
                Toate anunțurile {formatMakeDisplay(listing.make)}
              </Link>
            )}
            {citySlug && (
              <Link to={`/masini-de-vanzare-${citySlug}`} className="text-primary underline underline-offset-4">
                Mașini de vânzare în {cityLinkLabel(citySlug, getCityBySlug(citySlug, 'RO')?.name ?? listing.location_city ?? '', getCityBySlug(citySlug, 'RO')?.county)}
              </Link>
            )}
            <Link to="/masini-second-hand" className="text-primary underline underline-offset-4">
              Mașini pe mărci și orașe
            </Link>
          </nav>
        )}

        {/* Similar */}
        <section className="mt-12">
          <SimilarListings
            currentListingId={listing.id}
            make={listing.make}
            price={Number(listing.price)}
            categoryId={listing.category_id}
          />
        </section>
      </div>

      {/* Mobile sticky bottom contact bar */}
      <MobileContactBar
        phone={contact.phone}
        userId={listing.user_id || undefined}
        sellerType={(listing as any).seller_type}
        listingId={listing.id}
        onRequestClick={() => setReserveOpen(true)}
        address={[listing.location_city, listing.location_region].filter(Boolean).join(', ') || null}
        onMessageClick={() => {
          if (contact.email) {
            window.location.href = `mailto:${contact.email}?subject=${t.inquirySubject} ${title}`;
          } else {
            setReserveOpen(true);
          }
        }}
      />

      <ReservationModal
        open={reserveOpen}
        onOpenChange={setReserveOpen}
        listingId={listing.id}
        listingTitle={title}
        sellerId={listing.user_id}
        sellerType={(listing as any).seller_type}
      />
    </>
  );
}

const SpecRow: React.FC<{ label: string; value?: string | number | null }> = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between px-4 py-2.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground font-medium">{value}</span>
    </div>
  );
};
